import { Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import ClinicApi from "api/master/clinic.api";
import ReportApi from "api/report.api";
import VoucherApi from "api/voucher/voucher.api";
import HeaderCard from "component/CardCustom/HeaderCard";
import InputSecondNewRangePicker from "component/Input/InputSecondNewRangePicker";
import Loading from "component/Loading";
import _ from "lodash";
import moment from "moment";
import MainButton from "new-components/buttons/main-button";
import SelectInput from "new-components/inputs/select-input";
import TextInput from "new-components/inputs/text-input";
import PaginationTable from "new-components/pagination/pagination";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getBranch, numberFormat } from "utils/app.utils";
import { dateTimeToView } from "utils/date.utils";
import { UseSnCode } from "utils/useSnCode";

export default function GiftVoucherUsed() {
    const { t } = useTranslation()
    const momentNowRange = [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
    const momentDate = () => momentNowRange

    const [giftVoucherList, setGiftVoucherList] = useState([])
    const [giftVoucherType, setGiftVoucherType] = useState<any>([])
    const [giftVoucherTypeId, setGiftVoucherTypeId] = useState("ALL")
    const [optionGiftVoucher, setOptionGiftVoucher] = useState<any>([])
    const [giftVoucherId, setGiftVoucherId] = useState("ALL")
    const [giftVoucher, setGiftVoucher] = useState([])
    const [page, setPage] = useState(1)
    const [pageLimit, setPageLimit] = useState(10)
    const [rowCount, setRowCount] = useState(0)
    const [search, setSearch] = useState('')
    const [branch, setBranch] = useState<any>([])
    const [branchId, setBranchId] = useState<any>(getBranch())
    const [type, setType] = useState('EXTRA,DISCOUNT')
    const [loading, setLoading] = useState<boolean>(true)
    const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
    const [snType, setSnType] = useState(false)

    const fetchGiftVoucherList = async () => {
        setLoading(true)
        const condition = {
            page: page,
            pageLimit: pageLimit,
            sortBy: 'approvalId',
            sortType: 'ASC',
            branchId: branchId,
            search: search,
            type: type,
            voucherTypeId: giftVoucherTypeId === "ALL" ? null : giftVoucherTypeId,
            voucherId: giftVoucherId === "ALL" ? null : giftVoucherId,
            dateStart: rangeDate[0],
            dateEnd: rangeDate[1]
        }
        try {
            const resp: any = await ReportApi.reportGiftVoucher(condition)
            if (resp.status === 200) {
                setRowCount(resp?.headers['x-total'] || 0)
                setGiftVoucherList(resp.data?.rows)
                setGiftVoucher(resp.data?.group)
            } else {
                setGiftVoucherList([])
                setGiftVoucher([])
            }
        } catch (err) {
            console.log(err)
        }
        setLoading(false)
    }

    const fetchBranch = async () => {
        ClinicApi.findAllBranches()
            .then(({ data }) => setBranch(_.map(data, (item) => ({ value: item.branchId, name: item.branchName || item.branchNameEn })))).catch((e) => { return })
    }

    const fetchGiftVoucherType = async () => {
        VoucherApi.findAllType({
            page: 1,
            pageLimit: 100,
        }).then(({ data }) => setGiftVoucherType(_.map(data, (item) => ({ value: item.voucherTypeId, name: item.voucherTypeName })))).catch((e) => { return })
    }

    const fetchGiftVoucher = async () => {
        VoucherApi.findGiftVoucherList({
            voucherTypeId: giftVoucherTypeId === "ALL" ? null : giftVoucherTypeId
        }).then(({ data }) => setOptionGiftVoucher(_.map(data, (item) => ({ value: item.voucherId, name: item.voucherName })))).catch((e) => { return })
    }

    const handleSnCode = useCallback(() => {
        const fetchData = async () => {
            const sn = await UseSnCode()
            setSnType(sn)
        }
        fetchData()
    }, [snType])

    useEffect(() => {
        fetchGiftVoucherList()
    }, [page, pageLimit])

    useEffect(() => {
        fetchBranch()
        fetchGiftVoucherType()
        fetchGiftVoucher()
        handleSnCode()
    }, [])

    return (
        <Box>
            <Loading show={loading} />
            <HeaderCard text={t('REPORT.TABLE.GIFT_VOUCHER_USED.TITLE')} />
            <Box paddingX="24px" paddingY="16px">
                <Grid container spacing="16px" >
                    <Grid item xs={12} sm={4} md={2}>
                        <TextInput onChange={(e) => setSearch(e.target.value)} fullWidth label={t('REPORT.TABLE.GIFT_VOUCHER_USED.SEARCH')} value={search} />
                    </Grid>
                    <Grid xs={12} sm={4} item md={2}>
                        <SelectInput onChange={(e) => setBranchId(e.target.value)} value={branchId} label={t('REPORT.FILTER.BRANCH')} options={branch} defaultValue={branch?.[0]?.branchId} />
                    </Grid>
                    <Grid xs={12} sm={4} item md={2}>
                        <SelectInput onChange={(e) => setGiftVoucherTypeId(e.target.value)} label={t('REPORT.FILTER.GIFT_VOUCHER_USED.TYPE')} options={[{ value: 'ALL', name: t('REPORT.FILTER.ALL') }, ...giftVoucherType]} defaultValue='ALL' />
                    </Grid>
                    <Grid xs={12} sm={4} item md={2}>
                        <SelectInput onChange={(e) => setGiftVoucherId(e.target.value)} label={t('REPORT.FILTER.GIFT_VOUCHER_USED.PROMOTION')} options={[{ value: 'ALL', name: t('REPORT.FILTER.ALL') }, ...optionGiftVoucher]} defaultValue="ALL" />
                    </Grid>
                    <Grid xs={12} sm={4} item md={2}>
                        <InputSecondNewRangePicker
                            inputHeight={40}
                            value={rangeDate || ['', '']}
                            onchange={(val: any) => {
                                if (_.isEmpty(val)) setRangeDate(['', ''])
                                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                            }}
                            label={t('REPORT.FILTER.GIFT_VOUCHER_USED.DATE')}
                            allowClear
                            onClear={() => setRangeDate(['', ''])}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <MainButton onClick={() => fetchGiftVoucherList()} title={t('INPUT.SEARCH')} type="primary" variant="contained" style={{ width: '120px' }} />
                            {/* <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                                // if (e === 'Excel') onDownload()
                                // if (e === 'PDF') window.print()
                            }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} /> */}
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ overflowX: 'auto', overflowY: 'hidden', marginTop: '16px' }}>
                    <TableContainer style={{ boxShadow: 'none', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
                        <Table aria-label="simple table" sx={{ width: '100%' }}>
                            <TableHead sx={{ backgroundColor: "#2F4269", color: 'white', border: '1px solid #2F4269' }}>
                                <TableRow>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', minWidth: '100px', textAlign: 'center' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.NO')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'center' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.DATE_TIME')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', minWidth: '160px', textAlign: 'center' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.BILL_ID')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', minWidth: '100px', textAlign: 'center' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.PATIENT_CN_NUMBER')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', minWidth: '160px', textAlign: 'center' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.BRANCH_CN_NUMBER')}</TableCell>
                                    {snType && <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.PATIENT_SN_CODE')}</TableCell>}
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.PATIENT_NAME')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.GIFT_VOUCHER_TYPE')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.GIFT_VOUCHER')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'center', minWidth: '160px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.ID')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.VALUE')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'end' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.TOTAL_REDUCTION')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {giftVoucherList.length === 0 ?
                                    <TableRow key={0} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                                        <TableCell colSpan={11} sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{t('NOT_FOUND')}</TableCell>
                                    </TableRow>
                                    :
                                    giftVoucherList.map((data: any, index) => (
                                        <TableRow key={index} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{page * pageLimit - pageLimit + index + 1}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center', minWidth: '190px' }}>{dateTimeToView(data?.date)}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{data?.receiptNumber || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{data?.cnNumber || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{data?.branchCnNumber || "-"}</TableCell>
                                            {snType && <TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.snCode || "-"}</TableCell>}
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '200px' }}>{data?.patientName || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '200px' }}>{data?.voucherTypeName || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '400px' }}>{data?.voucherName || '-'}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{data.voucherCode || '-'}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '150px' }}>{data?.voucherPrice}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'end', minWidth: '130px' }}>{numberFormat(data?.voucherDiscount) || '0.00'}</TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box marginTop="8px">
                        <PaginationTable
                            onChange={(_e, newPage) => setPage(newPage)}
                            onPerPageChange={(e) => {
                                setPageLimit(parseInt(e.target.value, 10))
                                setPage(1)
                            }}
                            page={page}
                            perPage={pageLimit}
                            totalPage={rowCount}
                        />
                    </Box>
                </Box>

                <Box width="75%" marginTop="16px">
                    <TableContainer style={{ boxShadow: 'none', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
                        <Table aria-label="simple table" sx={{ width: '100%' }}>
                            <TableHead sx={{ backgroundColor: "#2F4269", border: '1px solid #2F4269' }}>
                                <TableRow>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', paddingX: '8px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.GIFT_VOUCHER_TYPE')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', paddingX: '8px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.GIFT_VOUCHER')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', paddingX: '8px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.VALUE')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'end', minWidth: '130px', paddingX: '8px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.UNIT_COUNT')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'end', paddingX: '8px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_USED.TOTAL_REDUCTION')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {giftVoucher.length === 0 ?
                                    <TableRow key={0} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                                        <TableCell colSpan={11} sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{t('NOT_FOUND')}</TableCell>
                                    </TableRow>
                                    :
                                    <>
                                        {giftVoucher.map((data: any, index) => (
                                            <TableRow key={index} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)', }}>
                                                <TableCell sx={{ fontSize: '16px', fontWeight: 400, verticalAlign: 'top', padding: '8px' }}>{data?.voucherTypeName || '-'}</TableCell>
                                                <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '190px', verticalAlign: 'top', padding: '8px' }}>
                                                    {_.map(data?.vouchers, (item, i: number) => (
                                                        <Typography marginTop={i !== 0 ? "16px" : 0}  >{item.voucherName || '-'}</Typography>
                                                    ))}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '16px', fontWeight: 400, verticalAlign: 'top', padding: '8px' }}>
                                                    {_.map(data?.vouchers, (item, i: number) => (
                                                        <Typography marginTop={i !== 0 ? "16px" : 0} minWidth="140px" >{item.value || '-'}</Typography>
                                                    ))}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'end', verticalAlign: 'top', padding: '8px' }}>
                                                    {_.map(data?.vouchers, (item, i: number) => (
                                                        <Typography marginTop={i !== 0 ? "16px" : 0} >{item.countUsed || '-'}</Typography>
                                                    ))}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'end', verticalAlign: 'top', padding: '8px' }}>
                                                    {_.map(data?.vouchers, (item, i: number) => (
                                                        <Typography marginTop={i !== 0 ? "16px" : 0} >{item.sumDiscount || 0}</Typography>
                                                    ))}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)', backgroundColor: '#E5E5E5' }}>
                                            <TableCell colSpan={3} align="right" sx={{ fontSize: '16px', fontWeight: 400 }}>
                                                <Typography>{t('REPORT.TABLE.PROMOTION_USED.TOTAL')}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ padding: '8px', fontSize: '16px', fontWeight: 400 }} align="right">{_.sum(_.map(giftVoucher, (item: any) => (_.sumBy(item.vouchers, 'countUsed'))))}</TableCell>
                                            <TableCell sx={{ padding: '8px', fontSize: '16px', fontWeight: 400 }} align="right">{_.sum(_.map(giftVoucher, (item: any) => (_.sumBy(item.vouchers, 'sumDiscount'))))}</TableCell>
                                        </TableRow>
                                    </>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box >
        </Box>
    )
}